import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { format, isBefore, startOfDay } from "date-fns";
import { FieldErrors, get, useFormContext, useFormState, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useGetFedexRating } from "@/api/rating.api";
import { Button } from "@/components/ui/button";
import { ControlledCustomTextarea } from "@/components/ui/customs/controlled-custom-textarea";
import {
  ExpeditionFedexApiData,
  QuotationUpdateBoFormData,
} from "@/types/forms/bo/expeditionBo.form";
import {
  FedexRatingResponseDto,
  GetFedexAccountResponseDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import {
  RatingResultCard,
  RatingSelection,
} from "@/components/backOffice/ratings/RatingResultCard";
import { FedexRateRecap } from "../../../finalize/fedexRateRecap";

export const FedexApiRates = ({
  fedexAccounts,
}: {
  fedexAccounts: GetFedexAccountResponseDto;
  expedition: GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const { mutateAsync: getFedexRating, isPending: isRatingPending } = useGetFedexRating();
  const [ratingResult, setRatingResult] = useState<FedexRatingResponseDto | null>(null);
  const [ratingSelection, setRatingSelection] = useState<RatingSelection | null>(null);
  const [ratesInvalidatedNotice, setRatesInvalidatedNotice] = useState(false);

  const fedexRate = useWatch({
    control: methods.control,
    name: "expeditionFedexApi.fedexRate",
  });
  const fedexAccountId = useWatch({
    control: methods.control,
    name: "expeditionFedexApi.fedexAccountId",
  });

  const { errors: formErrors } = useFormState({ control: methods.control });
  const fedexApiErrors = get(formErrors, "expeditionFedexApi") as
    | FieldErrors<ExpeditionFedexApiData>
    | undefined;

  const fedexAccount = fedexAccounts?.find((account) => account.id === fedexAccountId) ?? null;

  const handleRatingSelect = (selection: RatingSelection | null) => {
    setRatingSelection(selection);
    if (!selection) return;

    methods.setValue(
      "expeditionFedexApi.serviceType",
      selection.serviceType as ExpeditionFedexApiData["serviceType"],
      { shouldValidate: true },
    );

    const matchedAccount = fedexAccounts?.find((a) => a.id === selection.accountId);
    if (matchedAccount) {
      methods.setValue("expeditionFedexApi.fedexAccountId", matchedAccount.id, {
        shouldValidate: true,
      });
    }

    const selectedRate = ratingResult?.accountRatings
      ?.find((ar) => ar.account.accountId === selection.accountId)
      ?.rates?.find((r) => r.serviceType === selection.serviceType && !r.saturdayDelivery);

    if (selectedRate && ratingResult?.accountRatings?.length) {
      const allRates = ratingResult.accountRatings.flatMap((ar) =>
        (ar.rates ?? [])
          .filter((r) => !r.saturdayDelivery)
          .map((r) => ({ rate: r, account: ar.account })),
      );

      const cheapest = allRates.reduce<(typeof allRates)[number] | null>((best, cur) => {
        if (!best) return cur;
        return cur.rate.totalNetCharge < best.rate.totalNetCharge ? cur : best;
      }, null);

      if (cheapest && selectedRate.totalNetCharge > cheapest.rate.totalNetCharge) {
        const formatPrice = (value: number, currency = "EUR") =>
          new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(value);

        toast.warning(
          <div>
            <p className="font-semibold">{t("fedexRates.cheaperRateTitle")}</p>
            <p className="mt-1 text-sm">
              {t("fedexRates.selectedRate", {
                price: formatPrice(selectedRate.totalNetCharge, selectedRate.currency),
                serviceName: selection.serviceName,
              })}
            </p>
            <p className="text-sm">
              {t("fedexRates.cheaperRate", {
                price: formatPrice(cheapest.rate.totalNetCharge, cheapest.rate.currency),
                serviceName: cheapest.rate.serviceName,
                accountName: cheapest.account.accountName,
              })}
            </p>
          </div>,
          { autoClose: 8000 },
        );
      }
    }

    methods.setValue("expeditionFedexApi.fedexRate", selectedRate ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setRatesInvalidatedNotice(false);
  };

  const handleGetRating = async () => {
    methods.setValue("expeditionFedexApi.fedexRate", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("expeditionFedexApi.fedexAccountId", undefined as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("expeditionFedexApi.serviceType", undefined as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setRatingResult(null);
    setRatingSelection(null);
    setRatesInvalidatedNotice(false);

    const data = methods.getValues();
    if (data.apiEnum !== "FEDEX") return;
    const { expeditionPackaging } = data.expeditionFedexApi;
    const { deliveryInfo, shipperInfo } = data;

    const missing: string[] = [];

    const [isShipperValid, isDeliveryValid, isPackagingValid] = await Promise.all([
      methods.trigger("shipperInfo"),
      methods.trigger("deliveryInfo"),
      methods.trigger("expeditionFedexApi.expeditionPackaging"),
    ]);

    if (!isShipperValid) {
      missing.push(t("fedexRates.missingShipperInfo"));
    }
    if (!isDeliveryValid) {
      missing.push(t("fedexRates.missingDeliveryInfo"));
    }
    if (expeditionPackaging.length === 0 || !isPackagingValid) {
      missing.push(t("fedexRates.missingPackaging"));
    }
    if (
      !data.expeditionFedexApi.shipDate ||
      isBefore(startOfDay(data.expeditionFedexApi.shipDate), startOfDay(new Date()))
    )
      missing.push(t("fedexRates.missingShipDate"));

    if (missing.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">{t("fedexRates.missingFieldsTitle")}</p>
          <ul className="mt-1 list-disc pl-4 text-sm">
            {missing.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>,
        { autoClose: 8000 },
      );
      return;
    }

    const packages = expeditionPackaging.flatMap((pkg) =>
      Array.from({ length: pkg.count }, () => ({
        weight: pkg.weight,
        length: pkg.length,
        width: pkg.width,
        height: pkg.height,
        value: pkg.value,
      })),
    );

    const totalWeight = expeditionPackaging.reduce((sum, pkg) => sum + pkg.weight * pkg.count, 0);
    const totalDeclaredValue = expeditionPackaging.reduce(
      (sum, pkg) => sum + pkg.value * pkg.count,
      0,
    );

    try {
      const response = await getFedexRating({
        shipper: {
          streetLine1: shipperInfo.streetLines[0] || "",
          streetLine2: shipperInfo.streetLines[1],
          city: shipperInfo.city?.trim() || "",
          postalCode: shipperInfo.postalCode?.trim() || "",
          countryCode: shipperInfo.countryCode,
          stateOrProvinceCode: shipperInfo.stateOrProvinceCode,
        },
        recipient: {
          streetLine1: deliveryInfo.street,
          streetLine2: deliveryInfo.additionalInfo,
          city: deliveryInfo.city?.trim() || "",
          postalCode: deliveryInfo.postalCode?.trim() || "",
          countryCode: deliveryInfo.countryCode,
          stateOrProvinceCode: deliveryInfo.stateOrProvinceCode,
        },
        totalDeclaredValue,
        totalWeight,
        packages,
        shipDate: data.expeditionFedexApi.shipDate
          ? format(data.expeditionFedexApi.shipDate, "yyyy-MM-dd")
          : undefined,
      });
      setRatingResult(response);
    } catch (error) {
      const description =
        isAxiosError(error) && typeof error.response?.data?.message === "string"
          ? error.response.data.message
          : null;

      toast.error(
        description ? (
          <div className="max-w-sm break-words">
            <p className="font-semibold">{t("fedexRates.ratingError")}</p>
            <p className="mt-1 text-sm leading-snug font-normal">{description}</p>
          </div>
        ) : (
          t("fedexRates.ratingError")
        ),
        { className: description ? "!max-w-sm !w-full" : undefined },
      );
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <ControlledCustomTextarea
        label={t("fedexRates.deliveryInstructionsLabel")}
        control={methods.control}
        name="expeditionFedexApi.deliveryInstructions"
        className="w-full"
      />
      {ratesInvalidatedNotice && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t("fedexRates.ratesInvalidatedNotice")}
        </div>
      )}
      <Button type="button" disabled={isRatingPending} className="w-fit" onClick={handleGetRating}>
        {isRatingPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("fedexRates.calculating")}
          </>
        ) : (
          t("fedexRates.calculate")
        )}
      </Button>
      {(fedexApiErrors?.serviceType || fedexApiErrors?.fedexAccountId) && (
        <p className="text-sm text-red-500">{t("fedexRates.rateRequired")}</p>
      )}
      {!ratingResult && fedexRate && fedexAccountId && (
        <FedexRateRecap
          rate={fedexRate}
          accountId={fedexAccountId}
          accountName={fedexAccount?.accountName ?? ""}
          accountNumber={fedexAccount?.accountNumber ?? ""}
        />
      )}
      {ratingResult && (
        <div className="w-full">
          <RatingResultCard
            data={ratingResult}
            selection={ratingSelection}
            onSelect={handleRatingSelect}
          />
        </div>
      )}
    </div>
  );
};
